'use client';
import { PostListBlock as IPostListBlock } from '@/components/types';
import ContentContainer from '@/components/layout/ContentContainer';
import { generateLink } from '@/components/utils/getLinkTarget';
import { SupportedLanguages } from '@/types/globals';
import PostCard from '@/components/blocks/PostList/PostCard';
import clsx from 'clsx';
import Link from 'next/link';
import TriangleIcon from '@/assets/icon/triangle-right-icon.svg';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';
import { A11y, Navigation, Pagination } from 'swiper/modules';
import { useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getAllPosts } from '@/lib/wp';

const themes = {
  purple: {
    title: 'text-qf-purple',
    text: 'text-qf-black',
    link: 'text-teal-700',
    see_more: 'text-qf-purple',
    see_more_hover: 'hover:text-white hover:bg-qf-purple',
  },
  teal: {
    title: 'text-teal-700',
    text: 'text-qf-black',
    link: 'text-qf-purple',
    see_more: 'text-teal-700',
    see_more_hover: 'hover:text-white hover:bg-teal-700',
  },
  white: {
    title: 'text-white',
    text: 'text-white',
    link: 'text-teal-700',
    see_more: 'text-white',
    see_more_hover: 'hover:text-white hover:bg-teal-700',
  },
  black: {
    title: 'text-qf-black',
    text: 'text-qf-black',
    link: 'text-teal-700',
    see_more: 'text-qf-black',
    see_more_hover: 'hover:text-white hover:bg-qf-purple',
  },
};

const postListFields = [
  'id',
  'slug',
  'title.rendered',
  'excerpt.rendered',
  'acf.locale',
  'featured_image.full.url',
].join('');

export default function PostListBlock({
  block,
  locale = 'en',
}: {
  block: IPostListBlock;
  locale: SupportedLanguages;
}) {
  const swiperRef = useRef<SwiperType | null>(null);
  const paginationRef = useRef<HTMLDivElement>(null);
  const theme = themes[block.theme || 'black'];
  // fetch 3 blog posts by locale, filter out any that match a currentId
  const { data } = useQuery({
    queryKey: ['posts', locale],
    queryFn: () =>
      getAllPosts(
        'posts',
        `_fields=${postListFields}&per_page=5&orderby=date&order=desc`,
      ),
    enabled: block.post_list?.length < 3,
    staleTime: 60 * 1000, // 1 minute
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const currentPostIds = block.post_list?.map(post => post.id) || [];
  const additionalPosts =
    data
      ?.filter(
        post =>
          !currentPostIds.includes(post.id) && post.acf?.locale === locale,
      )
      ?.slice(0, 3 - currentPostIds.length)
      ?.map(post => ({
        id: post.id,
        title: post.title.rendered,
        excerpt: post.excerpt?.rendered || '',
        slug: post.slug,
        post_type: 'post' as const,
        featured_image: post.featured_image,
        locale,
      })) ?? [];

  // combine original posts and additional posts for swiper
  const allPosts = [...(block.post_list || []), ...additionalPosts];

  return (
    <ContentContainer additionalClassNames={'pt-4 pb-8'}>
      <div
        className={clsx(
          'relative flex flex-col gap-4',
          'sm:flex-row sm:justify-between',
        )}
      >
        <div className="space-y-4 sm:w-1/2 sm:max-w-lg">
          {block.pre_title && (
            <span
              className={clsx(
                'font-source-sans-pro text-[14px] leading-[24px] font-semibold tracking-[10%] uppercase',
                theme.title,
              )}
              dangerouslySetInnerHTML={{ __html: block.pre_title }}
            />
          )}
          {block.title && (
            <div className="my-2 max-w-sm">
              <span
                dangerouslySetInnerHTML={{ __html: block.title }}
                className={clsx(
                  'font-source-serif-pro text-[32px] leading-[130%] font-semibold tracking-[-1.4%]',
                  theme.text,
                )}
              />
            </div>
          )}
        </div>
        <div className="flex w-full flex-col justify-end space-y-4 sm:w-1/2 sm:self-auto">
          {block.copy?.length > 0 && (
            <div
              className={clsx(
                'font-source-sans-pro text-[17px] leading-[26px] font-normal',
                theme.text,
              )}
              dangerouslySetInnerHTML={{ __html: block.copy }}
            />
          )}
          {block.link_text && (
            <div
              className={clsx(
                'flex',
                !block.copy ? 'justify-end' : 'justify-start',
              )}
            >
              <Link
                href={generateLink(locale, 'post', '')}
                className={clsx(
                  'group inline-flex w-full items-center justify-center gap-2 rounded px-6 py-3 text-sm/5 font-semibold whitespace-nowrap transition sm:w-auto',
                  `border border-current bg-transparent ${theme.see_more}`,
                  theme.see_more_hover,
                )}
              >
                <span>{block.link_text}</span>
                <span className="">
                  <TriangleIcon width={9} height={9} />
                </span>
              </Link>
            </div>
          )}
        </div>
      </div>
      <div className="mt-8 gap-6">
        <Swiper
          modules={[Navigation, Pagination, A11y]}
          slidesPerView={3}
          slidesPerGroup={3}
          spaceBetween={30}
          autoHeight={true}
          onSwiper={swiper => {
            swiperRef.current = swiper;
          }}
          pagination={{
            clickable: true,
            el: paginationRef.current,
            bulletClass: 'w-4 h-1.5 rounded-3xl transition-all bg-white/50',
            bulletActiveClass: 'bg-white/100',
          }}
          breakpoints={{
            1024: { slidesPerView: 3, slidesPerGroup: 3, autoHeight: false },
            640: { slidesPerView: 2, slidesPerGroup: 2, autoHeight: false },
            0: { slidesPerView: 1, slidesPerGroup: 1, autoHeight: true },
          }}
          a11y={{ enabled: true }}
          className="mySwiper"
        >
          {allPosts?.map((post, index) => (
            <SwiperSlide key={`${post.id}-${index}`}>
              <PostCard
                title={post.title}
                excerpt={post.excerpt}
                link={generateLink(post.locale, post.post_type, post.slug)}
                image={post.featured_image?.full?.url}
                readMoreText={block.read_more_text || 'Read More'}
              />
            </SwiperSlide>
          ))}
        </Swiper>
        <div
          ref={paginationRef}
          className="custom-pagination flex justify-center gap-3 pt-4"
        />
      </div>
    </ContentContainer>
  );
}
